import { useContext, useEffect, useState } from 'react';

import Message from '../../../../components/ui/Message';
import { LoyaltyContext } from '../../../../context/loyalty/LoyaltyContext';
import { WalletModel, WalletMyDesign, WalletMyDesignResult, WalletProgram } from '../../../../models/interfaces';
import { WalletDesignForm } from './WalletDesignForm';
import { WalletGuidePanel } from './WalletGuidePanel';

interface WalletMyDesignsProps {
    tipo: WalletProgram;
    baseModels: WalletModel[];               // modelos de SwiftPay del mismo tipo (para "Basado en")
    canPreviewApple: boolean;                // la vista previa de iPhone requiere logo e ícono guardados
    busy: string | null;
    onPreviewApple: (codigo: string) => void;
    onPreviewGoogle: (codigo: string) => void;
    onChanged: () => Promise<void>;          // recarga la galería y la configuración
}

type Banners = Record<string, { apple?: string; google?: string }>;

// "Mis diseños": diseños propios del comercio para el programa (crear, editar, eliminar, usar y probar)
export const WalletMyDesigns = ({ tipo, baseModels, canPreviewApple, busy, onPreviewApple, onPreviewGoogle, onChanged }: WalletMyDesignsProps) => {
    const { loadMyDesigns, deleteMyDesign, saveWalletConfig, getWalletModelBanner } = useContext(LoyaltyContext);

    const [designs, setDesigns] = useState<WalletMyDesign[]>([]);
    const [max, setMax] = useState(5);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [version, setVersion] = useState(0);
    const [banners, setBanners] = useState<Banners>({});

    const [editing, setEditing] = useState<WalletMyDesign | null | undefined>(undefined);   // undefined = cerrado, null = crear
    const [toDelete, setToDelete] = useState<WalletMyDesign | null>(null);
    const [guide, setGuide] = useState<{ section?: string } | null>(null);
    const [working, setWorking] = useState<string | null>(null);
    const [notice, setNotice] = useState<{ mensaje: string; avisos: string[] } | null>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        const urls: string[] = [];
        (async () => {
            const resp = await loadMyDesigns(tipo);
            if (cancelled) return;
            setLoading(false);
            if (resp.error) { setLoadError(resp.error); return; }
            setLoadError('');
            setDesigns(resp.designs ?? []);
            if (resp.max) setMax(resp.max);
            // Los banners requieren el token: se descargan como Blob (iPhone y Android)
            for (const design of resp.designs ?? []) {
                (['apple', 'google'] as const).forEach(plataforma => {
                    getWalletModelBanner(design.codigo, plataforma).then(blob => {
                        if (!blob || cancelled) return;
                        const url = URL.createObjectURL(blob);
                        urls.push(url);
                        setBanners(prev => ({ ...prev, [design.codigo]: { ...prev[design.codigo], [plataforma]: url } }));
                    });
                });
            }
        })();
        return () => { cancelled = true; urls.forEach(url => URL.revokeObjectURL(url)); };
    }, [tipo, version]);

    // Después de crear, editar, eliminar o usar: se recargan el listado, la galería y la configuración
    const refreshAll = async () => {
        setBanners({});
        setVersion(v => v + 1);
        await onChanged();
    };

    const handleSaved = async (result: WalletMyDesignResult) => {
        setEditing(undefined);
        setNotice({ mensaje: result.mensaje || 'Diseño guardado.', avisos: result.avisos ?? [] });
        await refreshAll();
    };

    const handleUse = async (design: WalletMyDesign) => {
        setWorking(`use-${design.codigo}`);
        const resp = await saveWalletConfig(tipo, { modelo: design.codigo });
        setWorking(null);
        if (resp.error) { setError(resp.error); return; }
        setNotice({ mensaje: `Su tarjeta de ${tipo} ahora usa el diseño "${design.nombre}".`, avisos: resp.result?.avisos ?? [] });
        await refreshAll();
    };

    const handleDelete = async () => {
        const design = toDelete;
        setToDelete(null);
        if (!design) return;
        setWorking(`delete-${design.codigo}`);
        const resp = await deleteMyDesign(design.codigo);
        setWorking(null);
        if (resp.error) { setError(resp.error); return; }
        const avisos = resp.result?.avisos ?? [];
        setNotice({
            mensaje: resp.result?.estabaEnUso
                ? 'Tu tarjeta volvió al diseño predeterminado'
                : resp.result?.mensaje || `Se eliminó el diseño "${design.nombre}".`,
            avisos,
        });
        await refreshAll();
    };

    const limitReached = designs.length >= max;
    const disabled = busy !== null || working !== null;

    return (
        <div className="mb-4">
            <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-1">
                <h6 className="fw-semibold mb-0">Mis diseños <span className="text-muted fw-normal small">({designs.length} de {max})</span></h6>
                <div className="d-flex flex-wrap gap-2">
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setGuide({})}>
                        <i className="bi bi-question-circle me-1" />¿Cómo crear mi diseño?
                    </button>
                    <button type="button" className="btn btn-sm btn-primary" disabled={disabled || loading || limitReached} onClick={() => setEditing(null)}
                        title={limitReached ? `Ya tiene ${max} diseños propios de ${tipo}` : undefined}>
                        <i className="bi bi-plus-lg me-1" />Crear diseño
                    </button>
                </div>
            </div>
            <p className="small text-muted mb-3">
                Cree su propio diseño con un banner para iPhone, otro para Android y su color de marca. Solo usted lo ve, y aparece en la galería con la etiqueta "Propio".
            </p>
            {limitReached && (
                <div className="alert alert-warning py-2 small">
                    <i className="bi bi-exclamation-circle me-1" />
                    Ya tiene {max} diseños propios de {tipo}, el máximo permitido. Elimine uno que ya no use para crear otro.
                </div>
            )}

            {notice && (
                <div className="alert alert-success d-flex gap-2 align-items-start">
                    <i className="bi bi-check-circle-fill mt-1" />
                    <div className="flex-grow-1">
                        <div className="fw-semibold">{notice.mensaje}</div>
                        {notice.avisos.length > 0 && (
                            <ul className="small mb-0 ps-3 mt-1">
                                {notice.avisos.map((aviso, i) => <li key={i}>{aviso}</li>)}
                            </ul>
                        )}
                    </div>
                    <button type="button" className="btn-close" onClick={() => setNotice(null)} />
                </div>
            )}

            {loading ? (
                <div className="text-center py-3"><span className="spinner-border spinner-border-sm me-2" />Cargando mis diseños...</div>
            ) : loadError ? (
                <div className="alert alert-danger small">{loadError}</div>
            ) : designs.length === 0 ? (
                <div className="border rounded-3 p-4 text-center text-muted small">
                    <i className="bi bi-brush fs-4 d-block mb-1" />
                    Aún no tiene diseños propios de {tipo}. Pulse "Crear diseño" para hacer el primero.
                </div>
            ) : (
                <div className="row g-3">
                    {designs.map(design => (
                        <div className="col-lg-6" key={design.codigo}>
                            <div className={`card h-100 ${design.enUso ? 'border-success border-2' : ''}`}>
                                <div className="d-flex gap-1 p-1" style={{ background: design.colorFondo }}>
                                    {/* iPhone (1125×432) y Android (1032×812) con la misma altura */}
                                    {([['apple', 1125 / 432, 'iPhone'], ['google', 1032 / 812, 'Android']] as const).map(([plataforma, ratio, label]) => (
                                        <div key={plataforma} className="rounded overflow-hidden position-relative bg-light" style={{ flex: ratio, aspectRatio: String(ratio) }}>
                                            {banners[design.codigo]?.[plataforma]
                                                ? <img src={banners[design.codigo][plataforma]} alt={`Banner ${label}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                : <div className="h-100 d-flex align-items-center justify-content-center"><span className="spinner-border spinner-border-sm text-muted" /></div>}
                                            <span className="badge bg-dark bg-opacity-75 position-absolute bottom-0 start-0 m-1">
                                                <i className={`bi ${plataforma === 'apple' ? 'bi-apple' : 'bi-android2'}`} /> {label}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <div className="card-body d-flex flex-column">
                                    <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                                        <span className="fw-semibold">{design.nombre}</span>
                                        {[design.colorFondo, design.colorTexto, design.colorEtiqueta].map((color, i) => (
                                            <span key={i} className="rounded-circle border" style={{ width: 12, height: 12, background: color }} />
                                        ))}
                                        {design.enUso && <span className="badge bg-success-subtle text-success-emphasis">En uso</span>}
                                    </div>
                                    {design.descripcion && <p className="small text-muted mb-2">{design.descripcion}</p>}
                                    <div className="mt-auto d-flex flex-wrap gap-2 pt-2">
                                        {!design.enUso && (
                                            <button type="button" className="btn btn-sm btn-success" disabled={disabled} onClick={() => handleUse(design)}>
                                                {working === `use-${design.codigo}` ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-check2-circle me-1" />}
                                                Usar este diseño
                                            </button>
                                        )}
                                        <button type="button" className="btn btn-sm btn-outline-dark" disabled={disabled || !canPreviewApple}
                                            title={canPreviewApple ? 'Descarga la tarjeta de prueba (.pkpass) para abrirla en un iPhone' : 'Primero guarde el logo y el ícono de iPhone'}
                                            onClick={() => onPreviewApple(design.codigo)}>
                                            {busy === `apple-${design.codigo}` ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-apple me-1" />}
                                            Ver en iPhone
                                        </button>
                                        <button type="button" className="btn btn-sm btn-outline-success" disabled={disabled} onClick={() => onPreviewGoogle(design.codigo)}>
                                            {busy === `google-${design.codigo}` ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-android2 me-1" />}
                                            Ver en Android
                                        </button>
                                        <button type="button" className="btn btn-sm btn-outline-primary" disabled={disabled} onClick={() => setEditing(design)}>
                                            <i className="bi bi-pencil me-1" />Editar
                                        </button>
                                        <button type="button" className="btn btn-sm btn-outline-danger" disabled={disabled} onClick={() => setToDelete(design)}>
                                            {working === `delete-${design.codigo}` ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-trash me-1" />}
                                            Eliminar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editing !== undefined && (
                <WalletDesignForm tipo={tipo} design={editing} baseModels={baseModels}
                    banners={editing ? banners[editing.codigo] ?? {} : {}}
                    onClose={() => setEditing(undefined)} onSaved={handleSaved} onOpenGuide={(section) => setGuide({ section })} />
            )}
            {guide && <WalletGuidePanel section={guide.section} onClose={() => setGuide(null)} />}
            {toDelete && (
                <Message type="info" title="Eliminar diseño" okText="Eliminar" okWidth="100px"
                    message={toDelete.enUso
                        ? `"${toDelete.nombre}" es el diseño que usa su tarjeta hoy. Si lo elimina, la tarjeta volverá al diseño predeterminado. ¿Desea eliminarlo?`
                        : `¿Desea eliminar el diseño "${toDelete.nombre}"? Desaparecerá de su galería.`}
                    showCancel handleCancel={() => setToDelete(null)} closeDialog={() => setToDelete(null)}
                    handleOk={handleDelete} backgroundColor="rgba(0,0,0,0.4)" />
            )}
            {error.length > 0 && (
                <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />
            )}
        </div>
    );
};
